import { UUID } from "crypto";

import { DocumentVersion } from "@perceptron/utils/types";
import { JSONContent } from "@tiptap/react";

import { ComposeTemplate, getTemplateAssetCdnUrl, TEMPLATE_ASSET_UUIDS } from "@assets/templates";
import { AVAILABLE_MODELS } from "@demo/shared/types";
import { useEditorRefStore } from "@demo/store/editor-ref";
import { useMediaStore } from "@demo/store/media";
import { AgentState, useModelStore } from "@demo/store/model";
import { Role } from "@demo/types/base";
import { DocumentInputContentItem, DocumentInputContentV1 } from "@demo/types/documentContent";
import { ensureTipTapFormat } from "@demo/utils/document-content-converter";

import { BLANK_WITH_MEDIA_SET_CONTAINER } from "./editor";

/**
 * Sets document content in editors
 * @param documentUuid The document UUID to load content into
 * @param draftSystemContent The system editor content
 * @param draftUserContent The user editor content
 * Sets the content immediately in editors
 */
const setDocumentContentInEditors = (content: JSONContent): void => {
  const { setEditorContentFromJSON } = useEditorRefStore.getState();
  setEditorContentFromJSON(Role.USER, content);
};
/**
 * Applies a template's settings: its model, then the output format, reasoning and tools it asks for,
 * each only as far as the model supports it.
 * @param templateRequest The template request
 */
function setModelConfiguration(templateRequest: DocumentInputContentV1): void {
  try {
    const {
      model: templateModelName,
      annotationFormat,
      enableThinking,
      enableAudioInVideo,
      tools,
    } = templateRequest ?? {};

    if (!templateModelName) {
      console.warn("[setModelConfiguration] Missing template model name");
      return;
    }

    const {
      setOutputFormat,
      setEnableReasoning,
      setEnableAudioInVideo,
      setEnabledTools,
      setSelectedModel,
      setTemperature,
      selectedModel,
    } = useModelStore.getState();

    // Resolve model unless custom model url is provided
    const model =
      selectedModel.name === "custom-model"
        ? selectedModel
        : AVAILABLE_MODELS.find((m) => m.name === templateModelName);

    if (!model) {
      console.warn("[setModelConfiguration] Unknown model", templateModelName);
      return;
    }

    setSelectedModel(model);
    setEnableReasoning(!!enableThinking && !!model.capabilities?.reasoning?.supported);
    setEnableAudioInVideo(enableAudioInVideo);
    setEnabledTools((tools ?? []).filter((family) => model.capabilities.tools.includes(family)));
    // The template's temperature is not copied here: it belongs to the example, and a run uses it only
    // while the prompt is still exactly the example (`libs/model.ts`). Clearing the store's value, which
    // only the developer temperature input sets, lets it apply.
    setTemperature(undefined);
    const outputFormatIndex = annotationFormat
      ? model.outputFormats.findIndex((format) => format.value === annotationFormat.toUpperCase())
      : -1;
    setOutputFormat(Math.max(outputFormatIndex, 0));
  } catch (error) {
    console.error("[setModelConfiguration] Failed to apply model configuration from template", error);
  }
}

/**
 * Clears document-related stores
 */
const clearDocumentStores = () => {
  const { clearMediaSets } = useMediaStore.getState();

  clearMediaSets();
};

export const insertTemplateIntoDocument = async (template: ComposeTemplate) => {
  clearDocumentStores();

  const mediaDownloadUrls = new Map<UUID, string>();

  TEMPLATE_ASSET_UUIDS.forEach((assetUuid) => {
    mediaDownloadUrls.set(assetUuid as UUID, getTemplateAssetCdnUrl(assetUuid as UUID));
  });

  const documentJson = ensureTipTapFormat(template.request.user, "user", DocumentVersion.Draft, mediaDownloadUrls);

  setDocumentContentInEditors(documentJson);
  setModelConfiguration(template.request);
};

/**
 * Gets the number of images in a document
 * @param contents The document contents
 * @returns The number of images in the document
 */
export const getNumberOfImages = (contents: DocumentInputContentItem[]) => {
  return contents.reduce((acc, item) => {
    if (item.type === "media") {
      return (
        acc +
        item.value.reduce((acc, media) => {
          if (media.type === "image") {
            return acc + media.value.annotations.points.length + media.value.annotations.boundingBoxes.length;
          }
          return acc;
        }, 0)
      );
    }
    return acc;
  }, 0);
};

/**
 * Gets the number of annotations in a document
 * @param contents The document contents
 * @returns The number of annotations in the document
 */
export const getNumberOfAnnotations = (contents: DocumentInputContentItem[]) => {
  return contents.reduce((acc, item) => {
    if (item.type === "media") {
      return (
        acc +
        item.value.reduce((acc, media) => {
          if (media.type === "image") {
            return acc + media.value.annotations.points.length + media.value.annotations.boundingBoxes.length;
          }
          return acc;
        }, 0)
      );
    }
    return acc;
  }, 0);
};

export const getPromptCharacterCount = (contents: DocumentInputContentItem[]) => {
  return contents.reduce((acc, item) => {
    if (item.type === "paragraph") {
      return acc + item.value.value.length;
    }
    return acc;
  }, 0);
};

export const clearDocument = () => {
  const { clearMediaSets } = useMediaStore.getState();
  const { clearAgentOutputAndSetAgentState, stopStreaming, setEnableAudioInVideo } = useModelStore.getState();

  // Abort the in-flight stream first — otherwise chunks still in flight will
  // arrive after the state clear and repopulate the agent-output area.
  stopStreaming();
  clearAgentOutputAndSetAgentState(AgentState.IDLE);
  setEnableAudioInVideo(undefined);
  clearMediaSets();

  const { setEditorContentFromJSON } = useEditorRefStore.getState();
  setEditorContentFromJSON(Role.USER, BLANK_WITH_MEDIA_SET_CONTAINER);
};
